import { fromPairs } from 'lodash';
import { Point3Type } from '../gql/graphql';

function loadManifest(documentId: string): Promise<Autodesk.Viewing.Document> {
  return new Promise((resolve, reject) => {
    Autodesk.Viewing.Document.load(
      documentId,
      (doc) => {
        resolve(doc);
      },
      (error) => {
        reject(error);
      }
    );
  });
}

/**
 * Builds a THREE.Matrix4 from a 12-element nwModelToWorldTransform array.
 * The array format is: [r00, r01, r02, r10, r11, r12, r20, r21, r22, tx, ty, tz]
 */
function buildMatrix4FromNwTransform(transform: number[]): THREE.Matrix4 {
  const matrix = new THREE.Matrix4();
  // THREE.Matrix4.set() takes elements in row-major order for a column-major matrix
  matrix.set(
    transform[0],
    transform[3],
    transform[6],
    transform[9], // Column 1
    transform[1],
    transform[4],
    transform[7],
    transform[10], // Column 2
    transform[2],
    transform[5],
    transform[8],
    transform[11], // Column 3
    0,
    0,
    0,
    1 // Column 4
  );
  return matrix;
}

// Identity rotation values for comparison (3x3 identity matrix flattened)
const IDENTITY_ROTATION = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/**
 * Checks if the rotation part (first 9 elements) of a transform is identity.
 */
function isRotationIdentity(transform: number[]): boolean {
  return IDENTITY_ROTATION.every((val, i) => transform[i] === val);
}

/**
 * Extracts the nwModelToWorldTransform from a document's properties.
 */
function getNwTransformFromDocument(
  doc: Autodesk.Viewing.Document
): number[] | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootData = (doc.getRoot() as any).data;
  const viewable = rootData?.children?.[0];
  const props = viewable?.properties?.['Document Information'];
  const transform = props?.nwModelToWorldTransform;

  if (!transform || !Array.isArray(transform) || transform.length < 12) {
    return undefined;
  }
  return transform;
}

/**
 * Extracts the rotation-only part of nwModelToWorldTransform and returns its INVERSE.
 *
 * The nwModelToWorldTransform describes how to transform FROM model coordinates TO world coordinates.
 * However, since the model geometry is stored with that transformation already applied (e.g., rotated
 * for true north), we need to apply the INVERSE of the rotation to bring it back to the same
 * coordinate system as models with identity transforms.
 *
 * IMPORTANT: We only extract the ROTATION part of the transform, not the translation.
 * The translation in nwModelToWorldTransform often contains large world coordinates which,
 * when inverted, would cause floating-point precision issues. The translation is handled
 * separately via globalOffset.
 */
function extractRotationFromNwTransform(
  doc: Autodesk.Viewing.Document
): THREE.Matrix4 | undefined {
  try {
    const transform = getNwTransformFromDocument(doc);
    if (!transform || isRotationIdentity(transform)) {
      return undefined;
    }

    // Build a rotation-only matrix (zero out the translation component)
    const rotationOnlyTransform = [...transform.slice(0, 9), 0, 0, 0];
    const rotationMatrix = buildMatrix4FromNwTransform(rotationOnlyTransform);

    // Invert the rotation to correct the model's orientation
    return rotationMatrix.clone().invert();
  } catch (e) {
    console.error('Error extracting nwModelToWorldTransform:', e);
    return undefined;
  }
}

export type ViewerLoadModel = {
  id: string;
  urn: string;
  activeViewGuid?: string | null;
};

export const loadModels = async (
  view: Autodesk.Viewing.AggregatedView,
  models: ViewerLoadModel[],
  areOverlaysTemporary = false,
  globalOffset?: THREE.Vector3
): Promise<Record<string, Autodesk.Viewing.Model>> => {
  const documents = await Promise.all(
    models.map((m) => loadManifest(`urn:${m.urn}`))
  );

  // Extract rotation-only transforms from nwModelToWorldTransform in document properties
  // This is crucial for IFC models that have rotation (e.g., true north alignment)
  // We only extract the rotation, NOT the translation - globalOffset handles positioning
  const rotationTransforms = documents.map((doc) =>
    extractRotationFromNwTransform(doc)
  );

  const bubbleNodes = documents.map((doc, index) => {
    const bubbles = doc.getRoot().search({
      type: 'geometry',
      role: '3d',
    }) as Autodesk.Viewing.BubbleNode[];

    if (bubbles.length <= 1) {
      return bubbles[0] ?? null;
    }

    const activeGuid = models[index]?.activeViewGuid;
    if (activeGuid) {
      const matchingBubble = bubbles.find(
        (bubble) =>
          Array.isArray(bubble.children) &&
          bubble.children.some((child) => child?.data?.guid === activeGuid)
      );
      if (matchingBubble) {
        return matchingBubble;
      }
    }

    return bubbles[0] ?? null;
  });

  let loadedModels: Array<Autodesk.Viewing.Model>;

  // Check if any models need rotation correction
  const hasRotationTransforms = rotationTransforms.some((t) => t !== undefined);

  if (hasRotationTransforms) {
    // Load models individually to apply per-model rotation transforms
    //
    // IMPORTANT: Autodesk Viewer applies transforms in this order:
    // 1. globalOffset is applied first (translates geometry near origin)
    // 2. placementTransform is applied second (to the now-near-origin geometry)
    //
    // This means we can safely use a rotation-only placementTransform because
    // the geometry is already near origin when the rotation is applied.
    loadedModels = [];
    for (let i = 0; i < bubbleNodes.length; i++) {
      const node = bubbleNodes[i];
      if (!node) continue;

      const rotationTransform = rotationTransforms[i];
      const loadOptions: {
        keepCurrentModels: boolean;
        placementTransform?: THREE.Matrix4;
        globalOffset?: THREE.Vector3;
        applyRefPoint?: boolean;
      } = {
        keepCurrentModels: i > 0 || areOverlaysTemporary,
        applyRefPoint: false,
      };

      // Always apply globalOffset for floating-point precision
      if (globalOffset) {
        loadOptions.globalOffset = globalOffset;
      }

      // Apply rotation correction if this model has a non-identity rotation
      if (rotationTransform) {
        loadOptions.placementTransform = rotationTransform;
      }

      const model = await view.show(node, loadOptions);
      loadedModels.push(model);
    }
  } else if (areOverlaysTemporary) {
    loadedModels = await Promise.all(
      bubbleNodes.map((node) => view.show(node, { keepCurrentModels: true }))
    );
  } else {
    // No placement transforms needed, use efficient switchView
    loadedModels = await view.switchView(bubbleNodes, undefined);
  }

  return fromPairs(loadedModels.map((model) => [model.getData().urn, model]));
};

export async function initializeViewer(
  intoElement: HTMLDivElement,
  globalOffset: Point3Type
): Promise<Autodesk.Viewing.AggregatedView> {
  const aggregateView = new Autodesk.Viewing.AggregatedView();
  await aggregateView.init(intoElement, {
    getCustomLoadOptions: () => {
      return {
        applyRefPoint: false,
        globalOffset: new THREE.Vector3(
          globalOffset.x,
          globalOffset.y,
          globalOffset.z
        ),
      };
    },
  });

  await aggregateView.viewer.loadExtension('Autodesk.Snapping');
  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  if (window.Cypress) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.SPARKEL_3D_VIEWER = aggregateView.viewer;
  }
  return aggregateView;
}
